import React, { useEffect, useState } from "react";
import { Save, Gift, Trophy, Users, Receipt, Star, HelpingHand } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

const NUMBER = (v) => parseFloat(v) || 0;
const INT = (v) => parseInt(v) || 0;

export default function BenefitSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/benefit-settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const update = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        entry_gift_amount: NUMBER(s.entry_gift_amount),
        first_premium_cashback_percent: NUMBER(s.first_premium_cashback_percent),
        referral_reward: NUMBER(s.referral_reward),
        referral_max: INT(s.referral_max),
        agent_share_min_referrals: INT(s.agent_share_min_referrals),
        agent_share_percent: NUMBER(s.agent_share_percent),
        lucky_draw_min_months: INT(s.lucky_draw_min_months),
        lucky_draw_reward_percent: NUMBER(s.lucky_draw_reward_percent),
      };
      await api.patch("/admin/benefit-settings", payload);
      toast.success("Benefit settings saved");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid="benefit-settings-page">
      <h1 className="text-3xl font-extrabold tracking-tight">Benefit Settings</h1>
      <p className="text-sm text-slate-500">Configure rules & amounts for all 5 customer benefits</p>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card icon={Gift} tone="rose" title="Entry Gift" subtitle="Awarded after policy activation · Admin approves">
          <Field label="Gift Amount (₹)" testId="bs-entry-gift-amount" value={s.entry_gift_amount} onChange={(v) => update("entry_gift_amount", v)} type="number" />
        </Card>

        <Card icon={Receipt} tone="emerald" title="First Premium Cashback" subtitle="One-time · Admin approves after first payment">
          <Field label="Cashback %" testId="bs-first-cashback" value={s.first_premium_cashback_percent} onChange={(v) => update("first_premium_cashback_percent", v)} type="number" />
        </Card>

        <Card icon={Users} tone="cyan" title="Referral Income" subtitle="Auto-credited to referrer on referee's first payment">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reward per referral (₹)" testId="bs-referral-reward" value={s.referral_reward} onChange={(v) => update("referral_reward", v)} type="number" />
            <Field label="Max referrals (0 = unlimited)" testId="bs-referral-max" value={s.referral_max} onChange={(v) => update("referral_max", v)} type="number" />
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Currently: {INT(s.referral_max) === 0 ? "Unlimited" : `${s.referral_max} max`}</div>
        </Card>

        <Card icon={Star} tone="violet" title="Special Agent Share" subtitle="Eligible after N referrals complete yearly premium · Admin approves manual transfer">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min successful referrals" testId="bs-agent-share-min" value={s.agent_share_min_referrals} onChange={(v) => update("agent_share_min_referrals", v)} type="number" />
            <Field label="Share % of advisor commission" testId="bs-agent-share-percent" value={s.agent_share_percent} onChange={(v) => update("agent_share_percent", v)} type="number" />
          </div>
        </Card>

        <Card icon={Trophy} tone="amber" title="Lucky Draw" subtitle="Eligibility check enforced · Admin approves winner cashback">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min months tenure" testId="bs-lucky-min-months" value={s.lucky_draw_min_months} onChange={(v) => update("lucky_draw_min_months", v)} type="number" />
            <Field label="Reward % (default)" testId="bs-lucky-reward-percent" value={s.lucky_draw_reward_percent} onChange={(v) => update("lucky_draw_reward_percent", v)} type="number" />
          </div>
        </Card>

        <Card icon={HelpingHand} tone="indigo" title="Premium Finance Support" subtitle="Configurable per-request — set on Finance Support page (interest %, days)">
          <div className="text-xs text-slate-500">Defaults: <b>12% interest</b>, <b>100-day repayment</b>. Admin can override per request when approving.</div>
        </Card>
      </div>

      <button data-testid="save-benefit-settings" onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 bg-tenant-primary text-white px-6 py-3 rounded-2xl font-bold tap-scale disabled:opacity-60">
        <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
}

const Card = ({ icon: Icon, tone, title, subtitle, children }) => {
  const tones = {
    rose: "bg-rose-50 text-rose-600",
    emerald: "bg-emerald-50 text-emerald-600",
    cyan: "bg-cyan-50 text-cyan-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
    indigo: "bg-indigo-50 text-indigo-600",
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${tones[tone]} grid place-items-center shrink-0`}><Icon className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold tracking-tight">{title}</div>
          <div className="text-[11px] text-slate-500 leading-tight">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
};

const Field = ({ label, testId, value, onChange, type }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary font-bold" />
  </div>
);
